import { X, Receipt } from 'lucide-react';
import type { Invoice } from '@/api/invoices';

interface InvoiceDetailViewProps {
  invoice: Invoice;
  onClose: () => void;
}

function formatCurrency(amount: number, currency: string): string {
  try {
    return new Intl.NumberFormat(undefined, {
      style: 'currency',
      currency: currency || 'EUR',
    }).format(amount);
  } catch {
    return `${currency} ${amount.toFixed(2)}`;
  }
}

function statusBadge(status: Invoice['status']): { label: string; bg: string; color: string } {
  switch (status) {
    case 'paid':
      return { label: 'Paid', bg: '#DCFCE7', color: '#166534' };
    case 'sent':
      return { label: 'Sent', bg: '#DBEAFE', color: '#1E40AF' };
    case 'overdue':
      return { label: 'Overdue', bg: '#FEE2E2', color: '#991B1B' };
    case 'void':
      return { label: 'Void', bg: '#F3F4F6', color: '#374151' };
    default:
      return { label: 'Draft', bg: '#F3F4F6', color: '#374151' };
  }
}

export function InvoiceDetailView({ invoice, onClose }: InvoiceDetailViewProps) {
  const badge = statusBadge(invoice.status);
  const addr = invoice.businessAddress;

  return (
    <div
      style={{
        position: 'fixed',
        inset: 0,
        zIndex: 9999,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        backgroundColor: 'rgba(0,0,0,0.5)',
        padding: '16px',
      }}
      onClick={onClose}
    >
      <div
        style={{
          backgroundColor: '#fff',
          borderRadius: '16px',
          width: '100%',
          maxWidth: '560px',
          maxHeight: '90vh',
          overflow: 'hidden',
          display: 'flex',
          flexDirection: 'column',
          boxShadow: '0 25px 50px -12px rgba(0,0,0,0.25)',
        }}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div
          style={{
            background: 'linear-gradient(135deg, #6D28D9, #8B5CF6)',
            padding: '20px 24px',
            color: '#fff',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
          }}
        >
          <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
            <Receipt style={{ width: '28px', height: '28px' }} />
            <div>
              <h2 style={{ fontSize: '18px', fontWeight: 700, margin: 0 }}>Invoice</h2>
              <p style={{ fontSize: '12px', opacity: 0.85, marginTop: '2px', fontFamily: 'monospace' }}>
                {invoice.invoiceNumber}
              </p>
            </div>
          </div>
          <button
            onClick={onClose}
            style={{
              background: 'rgba(255,255,255,0.2)',
              border: 'none',
              borderRadius: '8px',
              padding: '6px',
              cursor: 'pointer',
              color: '#fff',
            }}
            aria-label="Close"
          >
            <X style={{ width: '20px', height: '20px' }} />
          </button>
        </div>

        {/* Body */}
        <div style={{ padding: '24px', overflowY: 'auto' }}>
          {/* Business block */}
          <div style={{ marginBottom: '16px' }}>
            <p style={{ fontSize: '11px', color: '#6B7280', textTransform: 'uppercase', letterSpacing: '0.05em', margin: 0 }}>
              From
            </p>
            <p style={{ fontSize: '15px', fontWeight: 600, color: '#111827', margin: '2px 0 0' }}>
              {invoice.businessName || 'Business'}
            </p>
            {addr && (
              <p style={{ fontSize: '12px', color: '#6B7280', marginTop: '2px', lineHeight: 1.4 }}>
                {[addr.line1, addr.line2, addr.city, addr.state, addr.postalCode, addr.country]
                  .filter(Boolean)
                  .join(', ')}
              </p>
            )}
          </div>

          {/* Customer */}
          <div style={{ marginBottom: '16px' }}>
            <p style={{ fontSize: '11px', color: '#6B7280', textTransform: 'uppercase', letterSpacing: '0.05em', margin: 0 }}>
              Bill To
            </p>
            <p style={{ fontSize: '15px', fontWeight: 600, color: '#111827', margin: '2px 0 0' }}>
              {invoice.customerName}
            </p>
            {invoice.customerEmail && (
              <p style={{ fontSize: '12px', color: '#6B7280' }}>{invoice.customerEmail}</p>
            )}
          </div>

          {/* Status + dates */}
          <div style={{ display: 'flex', gap: '16px', marginBottom: '20px', flexWrap: 'wrap' }}>
            <div>
              <p style={{ fontSize: '11px', color: '#6B7280', textTransform: 'uppercase', letterSpacing: '0.05em', margin: 0 }}>
                Status
              </p>
              <span
                style={{
                  display: 'inline-block',
                  marginTop: '4px',
                  padding: '4px 10px',
                  borderRadius: '999px',
                  fontSize: '12px',
                  fontWeight: 600,
                  backgroundColor: badge.bg,
                  color: badge.color,
                }}
              >
                {badge.label}
              </span>
            </div>
            <div>
              <p style={{ fontSize: '11px', color: '#6B7280', textTransform: 'uppercase', letterSpacing: '0.05em', margin: 0 }}>
                Issued
              </p>
              <p style={{ fontSize: '13px', fontWeight: 500, color: '#111827', marginTop: '4px' }}>
                {new Date(invoice.createdAt).toLocaleDateString()}
              </p>
            </div>
            {invoice.dueDate && (
              <div>
                <p style={{ fontSize: '11px', color: '#6B7280', textTransform: 'uppercase', letterSpacing: '0.05em', margin: 0 }}>
                  Due
                </p>
                <p style={{ fontSize: '13px', fontWeight: 500, color: '#111827', marginTop: '4px' }}>
                  {new Date(invoice.dueDate).toLocaleDateString()}
                </p>
              </div>
            )}
            {invoice.paidAt && (
              <div>
                <p style={{ fontSize: '11px', color: '#6B7280', textTransform: 'uppercase', letterSpacing: '0.05em', margin: 0 }}>
                  Paid
                </p>
                <p style={{ fontSize: '13px', fontWeight: 500, color: '#111827', marginTop: '4px' }}>
                  {new Date(invoice.paidAt).toLocaleDateString()}
                </p>
              </div>
            )}
          </div>

          {/* Line items */}
          <div
            style={{
              border: '1px solid #E5E7EB',
              borderRadius: '12px',
              overflow: 'hidden',
              marginBottom: '16px',
            }}
          >
            <div
              style={{
                display: 'grid',
                gridTemplateColumns: '2fr 60px 90px 90px',
                padding: '10px 14px',
                backgroundColor: '#F9FAFB',
                fontSize: '11px',
                color: '#6B7280',
                textTransform: 'uppercase',
                fontWeight: 600,
                letterSpacing: '0.05em',
              }}
            >
              <div>Description</div>
              <div style={{ textAlign: 'right' }}>Qty</div>
              <div style={{ textAlign: 'right' }}>Unit</div>
              <div style={{ textAlign: 'right' }}>Amount</div>
            </div>
            {invoice.lineItems.map((li, idx) => (
              <div
                key={idx}
                style={{
                  display: 'grid',
                  gridTemplateColumns: '2fr 60px 90px 90px',
                  padding: '10px 14px',
                  borderTop: '1px solid #F3F4F6',
                  fontSize: '13px',
                  color: '#111827',
                }}
              >
                <div>{li.description}</div>
                <div style={{ textAlign: 'right' }}>{li.quantity}</div>
                <div style={{ textAlign: 'right' }}>{formatCurrency(li.unitPrice, invoice.currency)}</div>
                <div style={{ textAlign: 'right', fontWeight: 500 }}>{formatCurrency(li.amount ?? (li as any).total ?? li.quantity * li.unitPrice, invoice.currency)}</div>
              </div>
            ))}
          </div>

          {/* Totals */}
          <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: '4px' }}>
            <div style={{ display: 'flex', gap: '16px', fontSize: '13px', color: '#6B7280' }}>
              <span>Subtotal</span>
              <span style={{ minWidth: '90px', textAlign: 'right', color: '#111827' }}>
                {formatCurrency(invoice.subtotal, invoice.currency)}
              </span>
            </div>
            {invoice.discountAmount > 0 && (
              <div style={{ display: 'flex', gap: '16px', fontSize: '13px', color: '#6B7280' }}>
                <span>Discount</span>
                <span style={{ minWidth: '90px', textAlign: 'right', color: '#111827' }}>
                  −{formatCurrency(invoice.discountAmount, invoice.currency)}
                </span>
              </div>
            )}
            {invoice.taxAmount > 0 && (
              <div style={{ display: 'flex', gap: '16px', fontSize: '13px', color: '#6B7280' }}>
                <span>Tax ({invoice.taxRate}%)</span>
                <span style={{ minWidth: '90px', textAlign: 'right', color: '#111827' }}>
                  {formatCurrency(invoice.taxAmount, invoice.currency)}
                </span>
              </div>
            )}
            <div
              style={{
                display: 'flex',
                gap: '16px',
                fontSize: '16px',
                fontWeight: 700,
                color: '#111827',
                marginTop: '8px',
                paddingTop: '8px',
                borderTop: '1px solid #E5E7EB',
                width: '100%',
                justifyContent: 'flex-end',
              }}
            >
              <span>Total</span>
              <span style={{ minWidth: '90px', textAlign: 'right' }}>
                {formatCurrency(invoice.total, invoice.currency)}
              </span>
            </div>
          </div>

          {invoice.notes && (
            <div style={{ marginTop: '16px', padding: '12px', backgroundColor: '#F9FAFB', borderRadius: '8px' }}>
              <p style={{ fontSize: '11px', color: '#6B7280', textTransform: 'uppercase', fontWeight: 600, margin: 0 }}>
                Notes
              </p>
              <p style={{ fontSize: '13px', color: '#374151', marginTop: '4px', whiteSpace: 'pre-wrap' }}>
                {invoice.notes}
              </p>
            </div>
          )}

          {/* Close */}
          <button
            onClick={onClose}
            style={{
              width: '100%',
              marginTop: '20px',
              padding: '12px',
              backgroundColor: '#F3F4F6',
              color: '#374151',
              border: 'none',
              borderRadius: '10px',
              fontSize: '14px',
              fontWeight: 600,
              cursor: 'pointer',
            }}
          >
            Close
          </button>
        </div>
      </div>
    </div>
  );
}
